import { useDispatch, useSelector } from 'react-redux'
import { useEffect } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { ReactComponent as InfoCircleIcon } from '../../../assets/images/common/icons/info-circle-icon-dark.svg'
import { getCommunityAccessControlCoach, createCommunityAccessControl, updateCommunityAccessControl } from '../../../actions/accessControlActions'
import { accessControlFormData, setField } from '../../../reducers/community-settings/registrationAccessCodeReducer'
import Input from '../../common/form/Input'
import Loader from '../../common/loader/legacy-loaders/Op36Loader-web'
import Toggle from '../../common/form/Toggle'
import CodeInput from '../../common/form/CodeInput'
import Tooltip from '../../common/Tooltip'
import AccessCodeFooter from './AccessCodeFooter'
import FieldError from '../../common/FieldError'

import classes from './RegistrationAccessCode.module.scss'

const RegistrationAccessCode = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const currentUser = useSelector(state => state.user.current_user)
  const community = currentUser.communities[0]
  const accessControlId = community.access_control_id

  const { isLoadingPage, id, enabled, description, code, errors } = useSelector(state => state.communitySettings.accessCodePage)
  const formData = useSelector(accessControlFormData)

  useEffect(() => {
    if (accessControlId) {
      dispatch(getCommunityAccessControlCoach({ communityId: community.id, accessControlId }))
    }

  }, [community, accessControlId, dispatch])

  const handleInputChange = (e) => {
    const field = e.target.name
    const value = e.target.value

    dispatch(setField({ field, value }))
  }

  const invalidFields = {
    description: description === '',
    code: code === '' || !/^\d{4}$/.test(code),
  }

  const formValid = () => {
    const customErrorMessages = {
      description: t('communitySettings.accessCode.descriptionRequired'),
      code: t('communitySettings.accessCode.codeDigitsRequired', { digits: 4 }),
    }

    const formErrors = {};

    ['description', 'code'].forEach((type) => {
      if (invalidFields[type]) {
        formErrors[type] = customErrorMessages[type]
      }
    })

    dispatch(setField({ field: 'errors', value: formErrors }))

    return Object.keys(formErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (formValid()) {
      const action = id
        ? updateCommunityAccessControl({ communityId: community.id, accessControlId: id, accessControlData: formData })
        : createCommunityAccessControl({ communityId: community.id, accessControlData: formData })

      dispatch(action).unwrap()
        .then(() => toast.success(t('communitySettings.accessCode.updateSuccess')))
        .catch(() => toast.error(t('communitySettings.common.genericError')))
    }
  }

  if (!community || isLoadingPage) {
    return <Loader message={t('communitySettings.accessCode.loadingPage')} />
  }

  return (
    <>
      <div className={classes.RegistrationAccessCodeTab}>
        <form id='accessControl' className={classNames('py-4 px-3', classes.form)} onSubmit={handleSubmit}>
          <div className='pb-4 d-flex align-items-center justify-content-between'>
            <label htmlFor='landingPageUrl'>{t('communitySettings.accessCode.restrictRegistration')}</label>

            <div>
              <InfoCircleIcon data-for='infoTooltip' data-tip className={classes.infoCircleIcon} width={20} height={20} />
              <Tooltip id='infoTooltip'
                effect='solid'
                delayUpdate={5}
                delayHide={5}
                place={'top'}
                className={classes.infoTooltip}
              >
                <p>{t('communitySettings.accessCode.tooltip')}</p>
              </Tooltip>
            </div>
          </div>

          <div className={classNames(classes.toggleWrapper, 'pb-4 d-flex align-items-center')}>
            <div className='d-flex align-items-center'>
              <Toggle
                name='enabled'
                checked={enabled}
                onChange={() => dispatch(setField({ field: 'enabled', value: !enabled }))}
              />

              <label className='ml-3' htmlFor='enabled'>{t('communitySettings.accessCode.enableAccessCode')}</label>
            </div>
          </div>

          <div className='pb-4'>
            <label htmlFor='description' className='d-flex justify-content-between align-items-center'>
              {t('common.field.description')}
            </label>
            <Input
              id='description'
              name='description'
              type='textarea'
              placeholder={t('communitySettings.accessCode.descriptionPlaceholder')}
              value={description}
              onChange={handleInputChange}
              error={errors.description}
              styles={{ inputContainer: { height: '100px' } }}
            />
          </div>

          <div className='pb-4'>
            <label htmlFor='accessCode' className='d-flex justify-content-between align-items-center'>
              {t('communitySettings.accessCode.enterAccessCode')}
            </label>

            <p className={classNames(classes.labelDescription, 'mb-4')}>
              {t('communitySettings.accessCode.enterAccessCodeHelper')}
            </p>

            <CodeInput
              name='accessCode'
              length={4}
              value={code}
              placeholder='1234'
              error={errors.code ? ' ' : ''}
              onChange={(value) => dispatch(setField({ field: 'code', value }))}
            />

            <FieldError message={errors.code} />
          </div>
        </form>
      </div>

      <AccessCodeFooter />
    </>
  )
}

export default RegistrationAccessCode
